import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listPipelines,
  getPipelineDetail,
  advanceStage,
  runPipelinePrompt,
  recordAction,
  createPipeline,
  archivePipeline,
  restorePipeline,
} from "@/api/pipeline";
import type { Pipeline, PipelineStatus } from "@career-ops-amplify/shared";
import { restorePipelineStatus } from "@/lib/pipeline-stages";

const PIPELINE_QUERY_KEY = ["pipeline"] as const;

export function usePipelines(params?: {
  limit?: number;
  cursor?: string;
  offset?: number;
}) {
  return useQuery({
    queryKey: [...PIPELINE_QUERY_KEY, "list", params ?? {}],
    queryFn: () => listPipelines(params),
    staleTime: 30_000,
  });
}

export function usePipelineDetail(pipelineId: string | undefined) {
  return useQuery({
    queryKey: [...PIPELINE_QUERY_KEY, "detail", pipelineId],
    queryFn: () => getPipelineDetail(pipelineId!),
    enabled: !!pipelineId,
    staleTime: 30_000,
  });
}

export function useAdvanceStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pipelineId, stageName }: { pipelineId: string; stageName: string }) =>
      advanceStage(pipelineId, stageName),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PIPELINE_QUERY_KEY });
      qc.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

export function usePipelineRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      pipelineId,
      promptKind,
      stageName,
    }: {
      pipelineId: string;
      promptKind: string;
      stageName?: string;
    }) => runPipelinePrompt(pipelineId, { promptKind, stageName }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PIPELINE_QUERY_KEY });
      qc.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

export function useRecordPipelineAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      pipelineId,
      action,
    }: {
      pipelineId: string;
      action: Parameters<typeof recordAction>[1];
    }) => recordAction(pipelineId, action),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PIPELINE_QUERY_KEY });
    },
  });
}

export function useCreatePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => createPipeline(jobId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PIPELINE_QUERY_KEY });
      qc.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

export function useArchivePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pipelineId: string) => archivePipeline(pipelineId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PIPELINE_QUERY_KEY });
    },
  });
}

export function useRestorePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pipeline: Pipeline) =>
      restorePipeline(pipeline.pipelineId ?? pipeline.id, restorePipelineStatus(pipeline)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PIPELINE_QUERY_KEY });
    },
  });
}

export function useUpdatePipelineStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      pipelineId,
      status,
    }: {
      pipelineId: string;
      status: PipelineStatus;
    }) => restorePipeline(pipelineId, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PIPELINE_QUERY_KEY });
    },
  });
}
